<script setup lang="ts">
import { submitFeedbackReport, type FeedbackReportReason } from '~/api/feedback'

const props = defineProps<{
  modelValue: boolean
  postsId: string
  reportReasons: FeedbackReportReason[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const appStore = useAppStore()

const { t } = useAppI18n()

const selectedReasonCode = ref('')
const isSubmittingReport = ref(false)
const isReportSuccessToastVisible = ref(false)
let reportSuccessToastTimer: ReturnType<typeof setTimeout> | null = null

const closeDialog = () => {
  emit('update:modelValue', false)
}

/** 展示举报成功局部提示。 */
const showReportSuccessToast = () => {
  if (reportSuccessToastTimer) {
    clearTimeout(reportSuccessToastTimer)
  }

  isReportSuccessToastVisible.value = true
  reportSuccessToastTimer = setTimeout(() => {
    isReportSuccessToastVisible.value = false
    reportSuccessToastTimer = null
  }, 3000)
}

/** 每次打开弹窗时默认选中第一项举报原因。 */
const selectFirstReportReason = () => {
  selectedReasonCode.value = props.reportReasons[0]?.code || ''
}

/** 提交举报内容，提交前校验帖子 ID、登录用户和举报原因。 */
const handleSubmitReport = async () => {
  isSubmittingReport.value = true

  try {
    await submitFeedbackReport({
      postsId: props.postsId,
      userId: appStore.user?.userId ?? '',
      reasonCode: selectedReasonCode.value,
    })

    closeDialog()
    showReportSuccessToast()
  } finally {
    isSubmittingReport.value = false
  }
}

onBeforeUnmount(() => {
  if (reportSuccessToastTimer) {
    clearTimeout(reportSuccessToastTimer)
  }
})

watch(
  () => [props.modelValue, props.reportReasons.length],
  ([isOpen]) => {
    if (isOpen) {
      selectFirstReportReason()
    }
  },
  { immediate: true },
)
</script>

<template>
  <Transition name="report-success-toast">
    <div v-if="isReportSuccessToastVisible" class="report-success-toast-wrap">
      <div class="report-success-toast" role="status" aria-live="polite">
        <CommonImage
          class="h-[20px] w-[20px] shrink-0"
          src="https://resources1.interface003.com/web30/2026/07/01/69b11f3a-1c49-496d-a036-b48f3be4c62e.png"
          alt=""
        />
        <span>{{ t('84407') }}</span>
      </div>
    </div>
  </Transition>

  <ElDialog
    :model-value="modelValue"
    width="min(564px, calc(100vw - 48px))"
    align-center
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    :z-index="3010"
    class="feedback-report-dialog"
    modal-class="feedback-report-dialog-mask"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <header class="feedback-report-dialog-header">
      <div class="feedback-report-dialog-title-row">
        <h2 id="feedback-report-dialog-title" class="feedback-report-dialog-title">{{ t('83782') }}</h2>
        <CommonImage
          class="h-[32px] w-[32px] cursor-pointer"
          src="https://resources1.interface003.com/web30/2026/06/08/2af6f5ee-7ed0-4d4b-8dff-dcf5540ccb14.png"
          @click="closeDialog"
        />
      </div>
    </header>

    <div v-if="props.reportReasons.length" class="feedback-report-dialog-body">
      <div class="flex w-full flex-wrap items-start">
        <div
          v-for="reason in props.reportReasons"
          :key="reason.code"
          class="mr-[32px] mb-[16px] flex shrink-0 cursor-pointer items-center space-x-[4px] rtl:mr-[0] rtl:ml-[32px]"
          @click="selectedReasonCode = reason.code"
        >
          <CommonImage
            class="h-[16px] w-[16px] shrink-0"
            :src="
              selectedReasonCode === reason.code
                ? 'https://resources1.interface003.com/web30/2026/07/06/1009fd88-c99a-4036-957c-3b2be7ba75d2.png'
                : 'https://resources1.interface003.com/web30/2026/07/06/2080a9c0-04b1-4b4f-9128-51bbe1554d0e.png'
            "
            alt=""
          />
          <div
            class="text-[14px] leading-[22px] font-[400] whitespace-nowrap"
            :class="selectedReasonCode === reason.code ? 'text-[#292a2e]' : 'text-[rgba(0,0,0,0.7)]'"
          >
            {{ reason.name }}
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-center bg-[#fff] px-[32px] pt-[4px] pb-[32px]"
      :class="{ 'pt-[20px]': !props.reportReasons.length }"
    >
      <button
        type="button"
        class="hover-mask-effect flex h-[46px] w-[184px] cursor-pointer items-center justify-center rounded-[1000px] border-0 bg-[#112d73] px-[64px] py-[12px] text-center text-[14px] leading-[22px] font-[600] whitespace-nowrap text-[#fff]"
        :class="isSubmittingReport ? 'opacity-60' : ''"
        @click="!isSubmittingReport && handleSubmitReport()"
      >
        {{ t('82922') }}
      </button>
    </div>
  </ElDialog>
</template>

<style scoped lang="less">
:global(.feedback-report-dialog-mask) {
  background: rgba(9, 20, 51, 0.48);
}

:global(.feedback-report-dialog) {
  max-height: calc(100vh - 64px);
  overflow: hidden;
  margin: 0;
  border-radius: 28px;
  background: #fff;
  padding: 0;
  box-shadow:
    0 0 1px rgba(30, 31, 33, 0.31),
    0 8px 12px rgba(30, 31, 33, 0.15);
}

:global(.feedback-report-dialog .el-dialog__header) {
  display: none;
  padding: 0;
  margin: 0;
}

:global(.feedback-report-dialog .el-dialog__body) {
  padding: 0;
}

.feedback-report-dialog-header {
  background: #fff;
  padding: 32px 32px 0;
}

.feedback-report-dialog-title-row {
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 8px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  padding-bottom: 16px;
}

.feedback-report-dialog-title {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feedback-report-dialog-body {
  display: flex;
  width: 100%;
  flex-direction: column;
  background: #fff;
  padding: 20px 32px 0;
}

.report-success-toast-wrap {
  position: fixed;
  top: 109px;
  left: 50%;
  z-index: 9999;
  transform: translateX(-50%);
  pointer-events: none;
}

.report-success-toast {
  min-width: 114px;
  min-height: 46px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 4px;
  background: #fff;
  color: #292a2e;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: nowrap;
}

.report-success-toast-enter-active,
.report-success-toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.report-success-toast-enter-from,
.report-success-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -12px);
}
.hover-mask-effect {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(255, 255, 255, 0.08);
    opacity: 0;
    transition: all 0.2s ease;
    pointer-events: none;
    border-radius: inherit;
  }

  // 悬浮状态：显示白色 8% 蒙层
  &:hover::after {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.08);
  }

  // 按下状态：以 0.2 秒的平滑过渡切换并显示黑色 10% 蒙层
  &:active::after {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.1);
  }
}
</style>
